import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  Box,
  Button,
  Stack,
  Text,
  Title,
  Paper,
  Badge,
  Group,
  Loader,
  Menu,
  ActionIcon,
} from "@mantine/core";
import classes from "./ScheduledRemindersPanel.module.css";
import {
  IconBell,
  IconDotsVertical,
  IconPlayerPlay,
  IconRefresh,
  IconSend,
  IconX,
} from "@tabler/icons-react";
import {
  notifyError,
  notifyInfo,
  notifySuccess,
  notifyWarning,
} from "../../../../../utils/notify";
import moment from "moment-timezone";
import { useApp } from "../../../../../context/AppContext";
import { getApiUrl, API_CONFIG } from "../../../../../config/api";
import { AppointmentScheduledMessage } from "../../../../../types/appointmentScheduledMessages";
import {
  STATUS_COLORS,
  SKIPPED_REASON,
  CHANGED_REASON,
  CANCELLED_REASON,
  PENDING_STATUSES,
  TERMINAL_STATUSES,
  POST_APPOINTMENT_DEFINITION_KEY,
  getScheduledMessageDisplayName,
} from "../../../../../utils/appointmentScheduledMessageDisplay";
import DeleteConfirmationModal from "../../../../common/DeleteConfirmationModal";
import { AppointmentMessagePreview } from "../../../../common/AppointmentMessagePreview";
import VerbalConfirmationModal from "./VerbalConfirmationModal";
import { useSendFormReminder } from "../../../../../hooks/useSendFormReminder";
import { useAppointmentScheduledMessagePreviews } from "../../../../../hooks/useAppointmentScheduledMessagePreviews";

interface ScheduledRemindersPanelProps {
  transactionUuid: string;
  timezone: string;
  canEdit?: boolean;
  /** False: no "Scheduled Reminders (N)" heading (the host shows the count). */
  showTitle?: boolean;
  /** Reports the current message count up to the parent (e.g. for a tab badge). */
  onCountChange?: (count: number) => void;
  /** Whether this appointment has at least one form the client still owes.
   *  When false, the manual "Send Form Reminder" button is hidden — there is
   *  nothing for the client to fill. */
  hasPendingForms?: boolean;
  /** The parent appointment's status (e.g. "Cancelled", "Confirmed"). Used
   *  to hide the manual-send button for cancelled appointments. */
  appointmentStatus?: string | null;
}

// Three-bucket grouping shown as collapsible sections in the Messages tab.
// Scheduled opens by default so staff see the live work first; the other two
// stay closed. CHANGED is pending-but-rescheduled (still going to fire) so it
// lives in Scheduled. CANCELLED is terminal-non-failure and rides along with
// Sent / Skipped — not a delivery error.
const SECTION_DEFS: ReadonlyArray<{
  key: string;
  label: string;
  statuses: ReadonlyArray<AppointmentScheduledMessage["status"]>;
}> = [
  {
    key: "scheduled",
    label: "Scheduled",
    statuses: ["SCHEDULED", "QUEUED", "SENDING", "CHANGED"],
  },
  {
    key: "sent_skipped",
    label: "Sent / Skipped",
    statuses: ["SENT", "SKIPPED", "CANCELLED"],
  },
  {
    key: "failed",
    label: "Failed",
    statuses: ["FAILED"],
  },
];

// Shape returned by POST /scheduled-messages/{uuid}/send. Pinned to the
// fields the compliance_core handler's final SELECT projects so future
// drift surfaces as a type error rather than a silent miss.
type SendNowResult = Pick<
  AppointmentScheduledMessage,
  "uuid" | "status" | "failure_reason" | "updated_at"
>;

export default function ScheduledRemindersPanel({
  transactionUuid,
  timezone,
  canEdit = true,
  showTitle = true,
  onCountChange,
  hasPendingForms = false,
  appointmentStatus = null,
}: ScheduledRemindersPanelProps) {
  const { fetchWithAuth } = useApp();
  const [messages, setMessages] = useState<AppointmentScheduledMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] =
    useState<AppointmentScheduledMessage | null>(null);
  const [cancelling, setCancelling] = useState(false);
  // Per-row rendered SMS body, fetched once per uuid and rendered inline under
  // each row's title. Shared with the chat "Scheduled messages" drawer via this
  // hook so the fetch + preview logic can't drift between the two surfaces.
  const previews = useAppointmentScheduledMessagePreviews(messages);

  // Hide the "Send Form Reminder" button when it isn't applicable. Joseph's
  // call (open-questions Q2): no greyed-out state, just absent.
  //   - no forms pending → nothing for the client to fill out
  //   - appointment cancelled → copy says "for this next appointment"
  // Past-end-time gating is deliberately NOT done here — see
  // `~/pagoda/prompts/forms_manual_send_part_2/todos.md` for the planned
  // tz-aware follow-up.
  const isCancelled = appointmentStatus === "Cancelled";
  const canSendFormReminder = canEdit && hasPendingForms && !isCancelled;

  const fetchMessages = useCallback(async (): Promise<
    AppointmentScheduledMessage[]
  > => {
    try {
      const url = getApiUrl(
        API_CONFIG.ENDPOINTS.APPOINTMENT_SCHEDULED_MESSAGES,
        "",
        { transactionUuid },
      );
      const data = await fetchWithAuth(url);
      // Hide CANCELLED messages entirely (e.g. reminders dropped by reconcile,
      // or messages cancelled on a terminal appointment) — they are noise in the
      // modal. SKIPPED/FAILED stay visible. Filtering here cascades to the
      // section buckets, the "(N)" title, and the Messages-tab badge.
      const list: AppointmentScheduledMessage[] = (data ?? []).filter(
        (m: AppointmentScheduledMessage) => m.status !== "CANCELLED",
      );
      setMessages(list);
      onCountChange?.(list.length);
      return list;
    } catch (err) {
      console.error("Failed to fetch scheduled messages:", err);
      notifyError("Could not load scheduled reminders.", { title: "Error" });
      return [];
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, transactionUuid, onCountChange]);

  // Manual "Send Form Reminder" flow (optimistic POST + opt-out verbal
  // confirmation) is shared with the calendar popover via this hook so the
  // consent handling can't drift. Aliased to the names used below.
  const {
    sending: sendingFormLink,
    verbalConfirmOpen,
    setVerbalConfirmOpen,
    sendReminder: handleSendFormReminder,
    confirmVerbalSend: handleVerbalConfirmSend,
  } = useSendFormReminder(transactionUuid, {
    onSent: async () => {
      // Refresh so the synthetic Form Reminder row the backend inserts shows
      // up immediately.
      await fetchMessages();
    },
  });

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleSendNow = async (msg: AppointmentScheduledMessage) => {
    setActionLoading(msg.uuid);
    try {
      const url = getApiUrl(
        API_CONFIG.ENDPOINTS.APPOINTMENT_SCHEDULED_MESSAGE_SEND,
        "",
        { transactionUuid, messageUuid: msg.uuid },
      );
      // The endpoint synchronously invokes the consumer Lambda and returns
      // the row's final {status, failure_reason}. A 2xx does NOT mean an
      // SMS went out — the consumer's compliance re-check can land the row
      // in SKIPPED (e.g. wrong-direction, quiet hours, opt-out) and the
      // delivery itself can land FAILED. Surface that to the staffer
      // rather than showing a misleading green toast.
      const result: SendNowResult | undefined = await fetchWithAuth(url, {
        method: "POST",
      });
      const finalStatus = result?.status;
      const displayName = getScheduledMessageDisplayName(msg);
      if (finalStatus === "SENT") {
        notifySuccess(`"${displayName}" sent successfully.`, {
          title: "Message Sent",
        });
      } else if (finalStatus === "SKIPPED") {
        // `||` rather than `??` so that a defensive empty-string
        // failure_reason from the backend falls through to the generic
        // copy instead of rendering an empty toast body.
        notifyWarning(
          result?.failure_reason ||
            `"${displayName}" was skipped at send time.`,
          { title: "Not Sent" },
        );
      } else if (finalStatus === "FAILED") {
        notifyError(
          result?.failure_reason || `Could not send "${displayName}".`,
          { title: "Send Failed" },
        );
      } else {
        // SENDING / SCHEDULED / undefined — synchronous invoke didn't reach
        // a terminal state. Let the polling loop reconcile the badge.
        notifyInfo(`"${displayName}" is being sent.`, { title: "Sending" });
      }
      // The SQS-triggered consumer Lambda processes the send asynchronously;
      // the DB status row may still be SENDING when we fetch immediately.
      // Refetch immediately for the SCHEDULED→SENDING transition, then poll
      // a couple times for the SENDING→SENT/FAILED transition so the UI
      // converges without a full page refresh. Break early once the target
      // row reaches a terminal state.
      await fetchMessages();
      const pollMs = [1000, 2000, 4000];
      for (const delay of pollMs) {
        await new Promise((r) => setTimeout(r, delay));
        const latest = await fetchMessages();
        const target = latest.find((m) => m.uuid === msg.uuid);
        if (target && TERMINAL_STATUSES.has(target.status)) break;
      }
    } catch (err: any) {
      console.error("Failed to send message:", err);
      notifyError(err?.errorBody?.message ?? "Could not send the message.", {
        title: "Send Failed",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetry = async (msg: AppointmentScheduledMessage) => {
    setActionLoading(msg.uuid);
    try {
      const url = getApiUrl(
        API_CONFIG.ENDPOINTS.APPOINTMENT_SCHEDULED_MESSAGE_RETRY,
        "",
        { transactionUuid, messageUuid: msg.uuid },
      );
      await fetchWithAuth(url, { method: "POST" });
      notifySuccess(`"${getScheduledMessageDisplayName(msg)}" rescheduled.`, {
        title: "Retry Scheduled",
      });
      await fetchMessages();
    } catch (err: any) {
      console.error("Failed to retry message:", err);
      notifyError(err?.errorBody?.message ?? "Could not retry the message.", {
        title: "Retry Failed",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const url = getApiUrl(
        API_CONFIG.ENDPOINTS.APPOINTMENT_SCHEDULED_MESSAGE_CANCEL,
        "",
        { transactionUuid, messageUuid: cancelTarget.uuid },
      );
      await fetchWithAuth(url, { method: "DELETE" });
      // Refetch BEFORE closing the inner DeleteConfirmationModal. Previously
      // we ran setCancelTarget(null) first, then await fetchMessages — that
      // ordering caused the parent TransactionDetailModal to close on some
      // browsers (suspected: focus/keyboard-handler race when the inner
      // Modal unmounts while a state-update batch is still in flight).
      // Refetching first keeps the inner modal mounted until all data is
      // settled, then we close in a single React tick.
      await fetchMessages();
      notifySuccess(
        `"${getScheduledMessageDisplayName(cancelTarget)}" cancelled.`,
        { title: "Reminder Cancelled" },
      );
      setCancelTarget(null);
    } catch (err: any) {
      console.error("Failed to cancel message:", err);
      notifyError(err?.errorBody?.message ?? "Could not cancel the reminder.", {
        title: "Cancel Failed",
      });
    } finally {
      setCancelling(false);
    }
  };

  const countLabel =
    loading || messages.length === 0 ? "" : ` (${messages.length})`;

  // Buckets are derived for the accordion. Same rows the API returned, just
  // partitioned by status so each section can render its own count + body.
  // All buckets ascend by scheduled_at; uuid tiebreaks for determinism.
  const buckets = useMemo(
    () =>
      SECTION_DEFS.map((section) => {
        const rows = messages.filter((m) =>
          section.statuses.includes(m.status),
        );
        const sorted = [...rows].sort((a, b) => {
          const aTime = a.scheduled_at ? Date.parse(a.scheduled_at) : 0;
          const bTime = b.scheduled_at ? Date.parse(b.scheduled_at) : 0;
          if (aTime !== bTime) return aTime - bTime;
          return a.uuid.localeCompare(b.uuid);
        });
        return { ...section, rows: sorted };
      }),
    [messages],
  );

  const renderMessageRow = (msg: AppointmentScheduledMessage) => {
    // Kebab is shown only when the row has a still-relevant action
    // (Send Now / Cancel on pending rows, Retry on FAILED). Preview is
    // now inline on every row, so terminal-only rows (SENT, CANCELLED,
    // SKIPPED) get no kebab — the menu would otherwise be empty.
    const hasActions =
      canEdit && (PENDING_STATUSES.has(msg.status) || msg.status === "FAILED");
    return (
      <Paper key={msg.uuid} p="md" withBorder>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap="xs" className={classes.rowContent}>
            <Group gap="xs">
              <Text fw={500}>{getScheduledMessageDisplayName(msg)}</Text>
              {/* Plain badge for every status — no hover tooltip. The
                  bottom-left text below explains anything that's not obvious
                  from the badge alone. */}
              <Badge
                color={STATUS_COLORS[msg.status] ?? "gray"}
                variant="light"
                size="sm"
              >
                {msg.status}
              </Badge>
              {msg.definition_key === POST_APPOINTMENT_DEFINITION_KEY && (
                <Badge color="teal" variant="light" size="sm">
                  Post-Appointment
                </Badge>
              )}
            </Group>
            <AppointmentMessagePreview
              preview={previews[msg.uuid]}
              status={msg.status}
            />
            <Text size="sm" c="dimmed">
              {msg.status === "SENT"
                ? `Sent ${moment.utc(msg.updated_at).tz(timezone).format("MMM D, YYYY h:mm A")}`
                : msg.status === "CANCELLED"
                  ? `Cancelled ${moment.utc(msg.updated_at).tz(timezone).format("MMM D, YYYY h:mm A")}`
                  : msg.status === "CHANGED"
                    ? `Rescheduled to ${moment.utc(msg.scheduled_at).tz(timezone).format("MMM D, YYYY h:mm A")}`
                    : `Scheduled for ${moment.utc(msg.scheduled_at).tz(timezone).format("MMM D, YYYY h:mm A")}`}
            </Text>
            {msg.status === "CHANGED" && msg.original_scheduled_at && (
              <Text size="xs" c="dimmed">
                Originally{" "}
                {moment
                  .utc(msg.original_scheduled_at)
                  .tz(timezone)
                  .format("MMM D, YYYY h:mm A")}{" "}
                (outside messaging hours)
              </Text>
            )}
            {/* Bottom-left explanation. SKIPPED + CHANGED + CANCELLED always
                show a fixed friendly explanation (replacing whatever raw
                camelcase / enum-shaped reason the backend may have stored).
                FAILED surfaces the backend failure_reason directly — that's
                where the actual error string for the staffer lives. */}
            {msg.status === "FAILED" && msg.failure_reason ? (
              <Text size="xs" c="red">
                {msg.failure_reason}
              </Text>
            ) : msg.status === "SKIPPED" ? (
              <Text size="xs" c="dimmed">
                {SKIPPED_REASON}
              </Text>
            ) : msg.status === "CHANGED" ? (
              <Text size="xs" c="dimmed">
                {CHANGED_REASON}
              </Text>
            ) : msg.status === "CANCELLED" ? (
              <Text size="xs" c="dimmed">
                {CANCELLED_REASON}
              </Text>
            ) : null}
          </Stack>
          {hasActions && (
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  loading={actionLoading === msg.uuid}
                  aria-label="Message actions"
                >
                  <IconDotsVertical size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {PENDING_STATUSES.has(msg.status) &&
                  msg.message_type !== "form_request" && (
                    <Menu.Item
                      leftSection={<IconPlayerPlay size={14} />}
                      onClick={() => handleSendNow(msg)}
                    >
                      Send Now
                    </Menu.Item>
                  )}
                {msg.status === "FAILED" && (
                  <Menu.Item
                    leftSection={<IconRefresh size={14} />}
                    onClick={() => handleRetry(msg)}
                  >
                    Retry
                  </Menu.Item>
                )}
                {PENDING_STATUSES.has(msg.status) && (
                  <Menu.Item
                    color="red"
                    leftSection={<IconX size={14} />}
                    onClick={() => setCancelTarget(msg)}
                  >
                    Cancel
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Paper>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      );
    }
    if (messages.length === 0) {
      return (
        <Paper
          p="md"
          withBorder
          style={{ borderColor: "#e8e8e8", borderStyle: "dashed" }}
        >
          <Stack gap="sm" align="center">
            <IconBell size={32} color="#c1c2c5" />
            <Text size="md" c="dimmed" ta="center">
              No scheduled reminders
            </Text>
          </Stack>
        </Paper>
      );
    }
    return (
      <Accordion multiple defaultValue={["scheduled"]} variant="default">
        {buckets.map((bucket) => (
          <Accordion.Item key={bucket.key} value={bucket.key}>
            <Accordion.Control className={classes.control}>
              <Text fw={500}>
                {bucket.label} ({bucket.rows.length})
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              {bucket.rows.length > 0 && (
                <Stack gap="md">{bucket.rows.map(renderMessageRow)}</Stack>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  };

  return (
    <>
      <Box>
        {(showTitle || canSendFormReminder) && (
          <Group justify={showTitle ? "space-between" : "flex-end"} mb="md">
            {showTitle && (
              <Title order={4}>Scheduled Reminders{countLabel}</Title>
            )}
            {canSendFormReminder && (
              <Button
                leftSection={<IconSend size={14} />}
                color="var(--brand-teal)"
                size="xs"
                onClick={handleSendFormReminder}
                loading={sendingFormLink}
              >
                Send Form Reminder
              </Button>
            )}
          </Group>
        )}
        {renderBody()}
      </Box>

      <VerbalConfirmationModal
        opened={verbalConfirmOpen}
        onClose={() => setVerbalConfirmOpen(false)}
        onConfirm={handleVerbalConfirmSend}
        isLoading={sendingFormLink}
      />

      <DeleteConfirmationModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        title="Cancel Reminder"
        message={
          cancelTarget
            ? `Cancel "${getScheduledMessageDisplayName(cancelTarget)}"? This cannot be undone.`
            : ""
        }
        isLoading={cancelling}
      />
    </>
  );
}
